import type { FormFieldOption as ModalFormFieldOption } from './schema';

/** Separator for composing a group key from a path; not valid inside a label. */
const GROUP_KEY_SEPARATOR = '\u0000';

/**
 * Identity of a group heading. Case- and whitespace-insensitive so source data that
 * spells the same heading two ways ("Yaris" / "YARIS") collapses into one group
 * rather than rendering twice. The label shown is the first spelling encountered.
 * It does NOT reconcile genuinely different strings ("Yaris" vs "Toyota Yaris 1,3")
 * — that is a data problem, not a display one.
 */
export function checklistGroupKey(path: readonly string[]): string {
  return path.map(segment => segment.trim().toLowerCase()).join(GROUP_KEY_SEPARATOR);
}

/** Every group key present in `options`, including intermediate ones. */
export function collectChecklistGroupKeys(options: readonly ModalFormFieldOption[]): string[] {
  const keys = new Set<string>();
  for (const option of options) {
    const path = option.groupPath ?? [];
    for (let i = 0; i < path.length; i++) {
      keys.add(checklistGroupKey(path.slice(0, i + 1)));
    }
  }
  return [...keys];
}

/** A rendered checklist line: either a collapsible group heading or a selectable option. */
export type ChecklistRow =
  | {
      kind: 'group';
      /** Stable identity of the heading (its full path) */
      key: string;
      label: string;
      /** Optional tag saying what kind of heading this is (see `groupBadges`) */
      badge?: string;
      /** Nesting depth, 0 for a top-level heading */
      depth: number;
      /** Values of every option below this heading, including nested groups */
      optionValues: string[];
    }
  | { kind: 'option'; option: ModalFormFieldOption; depth: number };

/**
 * Every value a single option row stands for: its own, plus any `mergedValues` it
 * collapses. Selection, counts and group totals all work in terms of these.
 */
export function checklistOptionValues(option: ModalFormFieldOption): string[] {
  return option.mergedValues?.length ? [option.value, ...option.mergedValues] : [option.value];
}

/** True when any option declares a `groupPath`, i.e. the checklist renders grouped. */
export function isGroupedChecklist(options: readonly ModalFormFieldOption[]): boolean {
  return options.some(option => (option.groupPath?.length ?? 0) > 0);
}

interface GroupNode {
  key: string;
  label: string;
  badge?: string;
  depth: number;
  children: Map<string, GroupNode>;
  options: ModalFormFieldOption[];
  optionValues: string[];
}

function createGroupNode(key: string, label: string, depth: number, badge?: string): GroupNode {
  return { key, label, badge, depth, children: new Map(), options: [], optionValues: [] };
}

/**
 * Flatten grouped options into rows: each group heading followed by its options, with
 * collapsed headings hiding their subtree. Options are emitted in input order within
 * their group, and groups in order of first appearance, so the caller controls sorting.
 * Options without a `groupPath` are emitted first, ungrouped.
 */
export function buildGroupedChecklistRows(
  options: readonly ModalFormFieldOption[],
  collapsedGroupKeys: readonly string[],
): ChecklistRow[] {
  const root = createGroupNode('', '', -1);

  for (const option of options) {
    const path = option.groupPath ?? [];
    let node = root;
    for (const [index, segment] of path.entries()) {
      const key = checklistGroupKey(path.slice(0, index + 1));
      let child = node.children.get(key);
      if (!child) {
        // First option to reach a heading fixes its display text and tag.
        child = createGroupNode(key, segment, index, option.groupBadges?.[index]);
        node.children.set(key, child);
      }
      node = child;
    }
    node.options.push(option);
  }

  // Bottom-up so a heading knows every value beneath it even while collapsed.
  const collectValues = (node: GroupNode): string[] => {
    const values = node.options.flatMap(option => checklistOptionValues(option));
    for (const child of node.children.values()) {
      values.push(...collectValues(child));
    }
    node.optionValues = values;
    return values;
  };
  collectValues(root);

  const collapsed = new Set(collapsedGroupKeys);
  const rows: ChecklistRow[] = [];

  const emit = (node: GroupNode): void => {
    for (const option of node.options) {
      rows.push({ kind: 'option', option, depth: node.depth + 1 });
    }
    for (const child of node.children.values()) {
      rows.push({
        kind: 'group',
        key: child.key,
        label: child.label,
        badge: child.badge,
        depth: child.depth,
        optionValues: child.optionValues,
      });
      if (!collapsed.has(child.key)) {
        emit(child);
      }
    }
  };
  emit(root);

  return rows;
}

export function buildChecklistChildValues(
  options: readonly ModalFormFieldOption[],
): Map<string, string[]> {
  const childValuesByParent = new Map<string, string[]>();

  for (const option of options) {
    if (!option.parentValue) {
      continue;
    }

    const childValues = childValuesByParent.get(option.parentValue) ?? [];
    childValues.push(option.value);
    childValuesByParent.set(option.parentValue, childValues);
  }

  return childValuesByParent;
}

export function buildVisibleChecklistOptions(
  options: readonly ModalFormFieldOption[],
  collapsedValues: readonly string[],
): ModalFormFieldOption[] {
  const childValuesByParent = buildChecklistChildValues(options);
  const collapsedValueSet = new Set(collapsedValues);
  const visible: ModalFormFieldOption[] = [];
  const visited = new Set<string>();
  const optionMap = new Map(options.map(option => [option.value, option]));

  const visit = (option: ModalFormFieldOption): void => {
    if (visited.has(option.value)) {
      return;
    }

    visited.add(option.value);
    visible.push(option);

    if (collapsedValueSet.has(option.value)) {
      return;
    }

    for (const childValue of childValuesByParent.get(option.value) ?? []) {
      const childOption = optionMap.get(childValue);
      if (childOption) {
        visit(childOption);
      }
    }
  };

  for (const option of options) {
    if (!option.parentValue || !optionMap.has(option.parentValue)) {
      visit(option);
    }
  }

  return visible;
}

export function collectChecklistSubtreeValues(
  options: readonly ModalFormFieldOption[],
  optionValue: string,
): string[] {
  const childValuesByParent = buildChecklistChildValues(options);
  const values = new Set<string>();

  const visit = (value: string): void => {
    if (values.has(value)) {
      return;
    }

    values.add(value);

    for (const childValue of childValuesByParent.get(value) ?? []) {
      visit(childValue);
    }
  };

  visit(optionValue);

  return Array.from(values);
}
